import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { CommonGrid } from 'styledComponents/common';



import dashboardStatIcon from '../../../assets/img/icons/dashboard-stat-icon.svg';
import { DashBoardCommonGrid } from '../../DashBoardNew/dashBoardNewStyles';
import DashboardStatCard from './DashboardStatCard';
import { currencySymbolMap } from '../../../utils/currencies';
import { getHoursWithSeconds } from '../../../utils/helper';
import CardHelpTooltip from 'components/Tooltip/CardHelpTooltip';
import ButtonHelpTooltip from 'components/Tooltip/ButtonHelpTooltip';


const DashBoardFirstSection = ({
  history,
  selectedOrganization,
  jobsInfoTodayIsloading,
  getJobInfoToday,
  jobsInfoToday,
  getDashboardInvoice,
  invoiceIsLoading,
  invoiceData,
  getDashboardProjectCount,
  projectCountIsLoading,
  projectCountData,
  getDashboardTimesheetCount,
  timesheetCountIsLoading,
  timesheetCountData,
}) => {
  const [jobTodayData, setJobTodayData] = useState(null);
  const [revenueData, setRevenueData] = useState(0);
  const [dueData, setDueData] = useState(0);
  const [invoiceCountData, setInvoiceCountData] = useState(0);
  const [totalProjectsData, setTotalProjectsData] = useState(0);
  const [totalTimeSheetSubmittedData, setTotalTimeSheetSubmittedData] = useState(0);
  const [totalTimeIn, setTotalTimeIn] = useState("0s");
  const currency = localStorage.getItem('currency') || 'USD';
  const currencySymbol = currencySymbolMap[currency] || '$';
  const orgType = selectedOrganization?.type || 'operation';
  const isOperationType = orgType === 'operation';

  //call the api to get the data
  useEffect(() => {
    if (selectedOrganization?.id) {
      const startTime = moment().format("YYYY-MM-DD"); // Format: YYYY-MM-DD
      const endTime = moment().format("YYYY-MM-DD"); // Same as start for today

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };
      getJobInfoToday(payload);
      getDashboardInvoice(payload);

      // Call project count API only for operation type organizations
      if (isOperationType && getDashboardProjectCount) {
        getDashboardProjectCount(payload);
      }

      // Call timesheet count API only for operation type organizations
      if (isOperationType && getDashboardTimesheetCount) {
        getDashboardTimesheetCount(payload);
      }
    }
  }, [selectedOrganization, isOperationType, getDashboardProjectCount, getDashboardTimesheetCount]);

  useEffect(() => {
    if (jobsInfoToday && Object.keys(jobsInfoToday).length) {
      setJobTodayData({
        title: isOperationType ? "Task Today" : "Job Today",
        value: jobsInfoToday.total_jobs || 0,
        secondaryText: "",
        icon: dashboardStatIcon,
      });
    }
  }, [jobsInfoToday]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return `${currencySymbol}0`;
    }

    try {
      // Try to use Intl.NumberFormat with the currency code
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return formatted;
    } catch (error) {
      // Fallback: manually format with symbol if Intl doesn't support the currency
      const formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return `${currencySymbol}${formattedValue}`;
    }
  };

  // Set invoice data from API response
  useEffect(() => {
    if (invoiceData && Object.keys(invoiceData).length) {
      setRevenueData(formatCurrency(Math.abs(invoiceData.revenue || 0)));
      setDueData(formatCurrency(Math.abs(invoiceData.due || 0)));
      setInvoiceCountData(Math.abs(invoiceData.invoice_count || 0));
    } else {
      setRevenueData(formatCurrency(0));
      setDueData(formatCurrency(0));
      setInvoiceCountData(0);
    }
  }, [invoiceData, currency, currencySymbol]);

  // Set project count data from API response
  useEffect(() => {
    if (projectCountData && Object.keys(projectCountData).length) {
      setTotalProjectsData(projectCountData.total_projects || 0);
    } else {
      setTotalProjectsData(0);
    }
  }, [projectCountData]);

  // Convert seconds to HH:MM:SS format for helper function
  const convertSecondsToTimeString = (seconds) => {
    if (!seconds || seconds === 0) return "0:0:0";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes}:${secs}`;
  };

  // Set timesheet count data from API response
  useEffect(() => {
    if (timesheetCountData && Object.keys(timesheetCountData).length) {
      const totalTimesheets = timesheetCountData.total_timesheets || 0;
      const totalTimeSeconds = timesheetCountData.total_time_seconds || 0;

      // Convert seconds to time format and format using helper function
      const timeString = convertSecondsToTimeString(totalTimeSeconds);
      const formattedTime = getHoursWithSeconds(timeString);

      // Display total timesheets with formatted time as secondary text or combine them
      // Based on requirement, showing total_timesheets as value
      setTotalTimeSheetSubmittedData(totalTimesheets);
      setTotalTimeIn(formattedTime);
    } else {
      setTotalTimeSheetSubmittedData(0);
      setTotalTimeIn("0s");
    }
  }, [timesheetCountData]);

  return (
    <DashBoardCommonGrid
      responsiveBreakpoint="768px"
      responsiveColumns="1fr"
      columns="1fr 1fr"
      gap="20px"
    >
      <CommonGrid disableResponsive={true} columns="1fr 1fr" gap="20px">
        {/* job Today */}
        <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = {`The total number of ${isOperationType ? 'tasks' : 'jobs'} booked today.`}>
          <DashboardStatCard
          title={jobTodayData?.title || ""}
          value={jobTodayData?.value || 0}
          secondaryText={jobTodayData?.secondaryText || ""}
          icon={jobTodayData?.icon || dashboardStatIcon}
          handleIconClick={() => isOperationType ? history.push(`/user/tasks`) : history.push(`/user/jobs`)}
          isLoading={jobsInfoTodayIsloading}
        />
        </ButtonHelpTooltip>
        
        {/* Revenue */}
        {!isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'Revenue accumulated today.'>
          <DashboardStatCard
          title="Revenue Today"
          value={revenueData}
          secondaryText=""
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/invoices`)}
          isLoading={invoiceIsLoading}
        />
        </ButtonHelpTooltip>
        }
        {/* total projects today */}
        {isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'The total number of projects created today.'>
          <DashboardStatCard
          title="Total Projects Created Today"
          value={totalProjectsData}
          secondaryText=""
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/project`)}
          isLoading={projectCountIsLoading}
        />
        </ButtonHelpTooltip>}
      </CommonGrid>
      <CommonGrid disableResponsive={true} columns="1fr 1fr" gap="20px">
        {/* total time sheet submitted today */}
        {isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'The total time sheets submitted today.'>
          <DashboardStatCard
          title="Total Time Sheet Submitted Today"
          value={totalTimeIn}
          secondaryText={`${totalTimeSheetSubmittedData} submitted`}
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/timesheet/daily`)}
          isLoading={timesheetCountIsLoading}
        />
        </ButtonHelpTooltip>}
        {/* due Today */}
        {!isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'Amount due today.'>
          <DashboardStatCard
          title="Due Today"
          value={dueData}
          secondaryText=""
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/invoices`)}
          isLoading={invoiceIsLoading}
        />
        </ButtonHelpTooltip>
        }
        {/* Invoice Raised Today */}
        {!isOperationType && <ButtonHelpTooltip transform='translateX(-50%) translateY(45%)'  enabled = {true} text = 'Number of invoices raised today.'>
          <DashboardStatCard
          title="Invoice Raised Today"
          value={invoiceCountData}
          secondaryText=""
          icon={dashboardStatIcon}
          handleIconClick={() => history.push(`/user/invoices`)}
          isLoading={invoiceIsLoading}
        />
        </ButtonHelpTooltip>
        }
      </CommonGrid>
    </DashBoardCommonGrid>
  );
};

export default DashBoardFirstSection;